import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { StepInfo } from '../types/protocol';

interface StepTrackerProps {
  steps: StepInfo[];
  workflowName: string | null;
  workflowDescription?: string | null;
  onStepClick?: (stepId: string) => void;
}

function StepIcon({ state }: { state: StepInfo['state'] }): React.ReactElement {
  switch (state) {
    case 'completed':
      return (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'prefilled':
      return (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
    case 'current':
      return (
        <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="12" />
        </svg>
      );
    case 'remaining':
      return (
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
  }
}

/** Convert a camelCase key to a readable label */
function humanizeKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

function formatGroupValue(value: Record<string, unknown>, t: TFunction): string {
  const entries = Object.entries(value);
  if (entries.length === 0) return '';
  const boolEntries = entries.filter(([, v]) => typeof v === 'boolean');
  const enabled = boolEntries.filter(([, v]) => v);
  if (enabled.length > 0) return enabled.map(([k]) => humanizeKey(k)).join(', ');
  if (boolEntries.length > 0) return t('recipe.noneEnabled');
  // Non-boolean group: show first meaningful value as a readable string
  for (const [k, v] of entries) {
    if (v !== '' && v !== null && v !== undefined) {
      if (Array.isArray(v)) {
        return `${humanizeKey(k)}: ${v.length}`;
      }
      if (typeof v === 'object') {
        return humanizeKey(k);
      }
      return `${humanizeKey(k)}: ${v}`;
    }
  }
  return t('recipe.defaults');
}

function formatValue(value: unknown, t: TFunction): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? t('fields.yes') : t('fields.no');
  if (Array.isArray(value)) return t('recipe.selected', { count: value.length });
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    // Try named objects first (server-resolved labels)
    if (obj.label) return String(obj.label);
    if (obj.name) return String(obj.name);
    // Flow builder data — show node count
    if (Array.isArray(obj.nodes)) {
      const nodeCount = (obj.nodes as unknown[]).length;
      return t('recipe.flowShort', { nodes: nodeCount });
    }
    // SelectMany with creates: {existing: [...], create: [...]}
    if (Array.isArray(obj.existing)) {
      const existingCount = (obj.existing as unknown[]).length;
      const createCount = Array.isArray(obj.create) ? (obj.create as unknown[]).length : 0;
      const parts: string[] = [];
      if (existingCount > 0) parts.push(t('recipe.existing', { count: existingCount }));
      if (createCount > 0) parts.push(t('recipe.newCount', { count: createCount }));
      return parts.join(', ') || t('recipe.none');
    }
    // Group field objects
    return formatGroupValue(obj, t);
  }
  return String(value);
}

export function StepTracker({ steps, workflowName, workflowDescription, onStepClick }: StepTrackerProps): React.ReactElement {
  const { t } = useTranslation();

  if (steps.length === 0) {
    return (
      <div className="sp-step-tracker sp-step-tracker-empty">
        <div className="sp-step-tracker-header">
          <h3 className="sp-step-tracker-title">{t('app.name')}</h3>
        </div>
        <p className="sp-step-tracker-placeholder">
          {t('steps.placeholder')}
        </p>
      </div>
    );
  }

  const completedCount = steps.filter((s) => s.state === 'completed' || s.state === 'prefilled').length;
  const progress = Math.round((completedCount / steps.length) * 100);

  return (
    <div className="sp-step-tracker">
      <div className="sp-step-tracker-header">
        <h3 className="sp-step-tracker-title">{workflowName ?? t('steps.workflow')}</h3>
        {workflowDescription && (
          <p className="sp-step-tracker-desc">{workflowDescription}</p>
        )}
        <div className="sp-progress-bar">
          <div className="sp-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="sp-progress-label">{t('steps.progress', { completed: completedCount, total: steps.length })}</span>
      </div>
      <ul className="sp-step-list">
        {steps.map((step) => {
          const clickable = onStepClick && step.state !== 'current';
          return (
            <li
              key={step.id}
              className={`sp-step sp-step-${step.state}`}
              onClick={clickable ? () => onStepClick(step.id) : undefined}
              onKeyDown={clickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onStepClick(step.id);
                }
              } : undefined}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
            >
              <span className="sp-step-icon"><StepIcon state={step.state} /></span>
              <span className="sp-step-label">{step.label}</span>
              {step.value !== undefined && step.value !== null && (
                <span className="sp-step-value">{formatValue(step.value, t)}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
